/* eslint-disable no-restricted-imports */
/*
 * Utilities for comparing dates.
 */

import {
    isAfter,
    isBefore,
    isEqual as isSameDate,
    isSameDay,
    isSameYear,
    isToday,
    isTomorrow,
    isWeekend,
} from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';

import { getDateFeatureConfig } from './DateConfigType';

export { isBefore, isAfter, isSameYear, isSameDay, isSameDate, isToday, isTomorrow, isWeekend };

const DEFAULT_TIMEZONE = 'Europe/Vienna';

/**
 * Checks if two dates are the same day in bank timezone.
 * @param {Date | number} dateLeft - The date to check.
 * @param {Date | number} dateRight - The date to compare against.
 * @returns {boolean} True if the date is the same day, false otherwise.
 */
export const isSameDayInBankTimezone = (
    dateLeft: Date | number,
    dateRight: Date | number,
): boolean => {
    const timezone = getDateFeatureConfig((f) => f?.base?.defaultTimezone) ?? DEFAULT_TIMEZONE;
    const parsedDateLeft = typeof dateLeft === 'number' ? new Date(dateLeft) : dateLeft;
    const parsedDateRight = typeof dateRight === 'number' ? new Date(dateRight) : dateRight;

    const leftZoned = utcToZonedTime(parsedDateLeft, timezone);
    const rightZoned = utcToZonedTime(parsedDateRight, timezone);

    return (
        leftZoned.getFullYear() === rightZoned.getFullYear() &&
        leftZoned.getMonth() === rightZoned.getMonth() &&
        leftZoned.getDate() === rightZoned.getDate()
    );
};

/**
 * Checks if a date is the same day or before another date.
 * @param {Date | number} date - The date to check.
 * @param {Date | number} dateToCompare - The date to compare against.
 * @returns {boolean} True if the date is the same day or before the compared date, false otherwise.
 */
export const isSameDayOrBefore = (date: Date | number, dateToCompare: Date | number): boolean => {
    return isSameDay(date, dateToCompare) || isBefore(date, dateToCompare);
};

/**
 * Checks if a date is the same day or after another date.
 * @param {Date | number} date - The date to check.
 * @param {Date | number} dateToCompare - The date to compare against.
 * @returns {boolean} True if the date is the same day or after the compared date, false otherwise.
 */
export const isSameDayOrAfter = (date: Date | number, dateToCompare: Date | number): boolean => {
    return isSameDay(date, dateToCompare) || isAfter(date, dateToCompare);
};

/**
 * Checks if a date is strictly before another date (not including the same day).
 * @param {Date | number} date - The date to check.
 * @param {Date | number} dateToCompare - The date to compare against.
 * @returns {boolean} True if the date is strictly before the compared date, false otherwise.
 */
export const isDayBefore = (date: Date | number, dateToCompare: Date | number): boolean => {
    return !isSameDay(date, dateToCompare) && isBefore(date, dateToCompare);
};

/**
 * Checks if a date is strictly after another date (not including the same day).
 * @param {Date | number} date - The date to check.
 * @param {Date | number} dateToCompare - The date to compare against.
 * @returns {boolean} True if the date is strictly after the compared date, false otherwise.
 */
export const isDayAfter = (date: Date | number, dateToCompare: Date | number): boolean => {
    return !isSameDay(date, dateToCompare) && isAfter(date, dateToCompare);
};

/**
 * Checks if a date is between two other dates, inclusive of the start and end dates.
 * @param {Date | number} date - The date to check.
 * @param {Date | number} dateStart - The start date of the range.
 * @param {Date | number} dateEnd - The end date of the range.
 * @returns {boolean} True if the date is between or equal to the start and end dates, false otherwise.
 */
export const isBetweenInDays = (
    date: Date | number,
    dateStart: Date | number,
    dateEnd: Date | number,
): boolean => {
    if (isSameDayOrAfter(date, dateStart) && isSameDayOrBefore(date, dateEnd)) {
        return true;
    }
    return false;
};

/**
 * Returns the earlier of two dates.
 * @param {Date} date - The first date to compare.
 * @param {Date} dateToCompare - The second date to compare.
 * @returns {Date} The earlier of the two input dates.
 */
export const getMinDate = (date: Date, dateToCompare: Date): Date => {
    if (isBefore(date, dateToCompare)) {
        return date;
    }
    return dateToCompare;
};

/**
 * Returns the later of two dates.
 * @param {Date} date - The first date to compare.
 * @param {Date} dateToCompare - The second date to compare.
 * @returns {Date} The later of the two input dates.
 */
export const getMaxDate = (date: Date, dateToCompare: Date): Date => {
    if (isBefore(date, dateToCompare)) {
        return dateToCompare;
    }
    return date;
};
